import React from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PricingFeature {
  name: string;
}

type ButtonType = 'subscribe' | 'upgrade' | 'downgrade' | 'saveWithYearly' | 'current';

interface PricingCardProps {
  productName: string;
  description: string;
  price: string;
  monthlyPrice?: string;
  buttonText: string;
  features: (PricingFeature | string)[];
  onSubscribe: () => void;
  isLoading?: boolean;
  billingPeriod: 'monthly' | 'yearly';
  isCurrentPlan?: boolean;
  buttonType?: ButtonType;
}

export const PricingCard: React.FC<PricingCardProps> = ({
  productName,
  description,
  price,
  monthlyPrice,
  buttonText,
  features,
  onSubscribe,
  isLoading = false,
  billingPeriod,
  isCurrentPlan = false,
  buttonType = 'subscribe'
}) => {
  const { t } = useTranslation();

  const getButtonText = () => {
    switch (buttonType) {
      case 'current':
        return t('billing.manageSubscription');
      case 'upgrade':
        return t('billing.upgrade');
      case 'downgrade':
        return t('billing.downgrade');
      case 'saveWithYearly':
        return t('billing.saveWithYearly');
      default:
        return buttonText;
    }
  };

  const getButtonStyle = () => {
    switch (buttonType) {
      case 'current':
        return 'bg-purple-100 text-purple-700 cursor-not-allowed';
      case 'upgrade':
        return 'bg-green-600 text-white hover:bg-green-700';
      case 'downgrade':
        return 'bg-gray-500 text-white hover:bg-gray-600';
      case 'saveWithYearly':
        return 'bg-green-600 text-white hover:bg-green-700';
      default:
        return 'bg-primary-600 text-white hover:bg-primary-700';
    }
  };
  return (
    <div className={`relative rounded-2xl border p-6 ${
      buttonType === 'current'
        ? 'bg-purple-50 border-purple-500 border-2' 
        : 'bg-white border-black'
    }`}>
      <div className="absolute -top-3 left-4">
        <span className={`inline-block px-4 py-1 text-xs font-medium rounded-full ${
          buttonType === 'current'
            ? 'bg-purple-100 text-purple-700' 
            : 'bg-orange-100 text-orange-700'
        }`}>
          {productName}
        </span>
      </div>
      {buttonType === 'current' && (
        <div className="absolute -top-3 right-4">
          <span className="inline-block px-3 py-1 bg-purple-600 text-white text-xs font-medium rounded-full">
            {t('billing.currentPlanBadge')}
          </span>
        </div>
      )}
      <div className="mb-6">
        {billingPeriod === 'yearly' ? (
          <>
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold">{monthlyPrice}</span>
              <span className="text-gray-500">{t('billing.perMonth')}</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {t('billing.billedAnnually')} {price}
            </p>
          </>
        ) : (
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-bold">{price}</span>
            <span className="text-gray-500">{t('billing.perMonth')}</span>
          </div>
        )}
      </div>
      <h3 className="text-xl font-semibold mb-2">{productName}</h3>
      <p className="text-sm text-gray-600 mb-6">{description}</p>
      <button
        onClick={onSubscribe}
        disabled={isLoading || buttonType === 'current'}
        className={`w-full py-2 px-4 rounded-lg transition-colors mb-6 disabled:opacity-50 disabled:cursor-not-allowed ${getButtonStyle()}`}
      >
        {isLoading ? t('common.loading', 'Loading...') : getButtonText()}
      </button>
      <ul className="space-y-4">
        {features?.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <span className="text-gray-600">{typeof feature === 'string' ? feature : feature.name}</span>
          </li>
        )) || []}
      </ul>
    </div>
  );
};